import type { ReactNode } from 'react';
import Reveal from '@/components/base/Reveal';

interface StepSectionProps {
  id?: string;
  index: string;
  stage?: 'ingest' | 'filter' | 'execute' | 'record';
  title: string;
  subtitle: string;
  body?: string;
  children: ReactNode;
  reverse?: boolean;
  band?: boolean;
}

const stageLabels: Record<NonNullable<StepSectionProps['stage']>, string> = {
  ingest: 'ENGINE · INGEST',
  filter: 'ENGINE · FILTER',
  execute: 'ENGINE · EXECUTE',
  record: 'ENGINE · RECORD',
};

function StepSection({
  id,
  index,
  stage,
  title,
  subtitle,
  body,
  children,
  reverse = false,
  band = false,
}: StepSectionProps) {
  return (
    <section
      id={id}
      className={`w-full px-4 md:px-8 lg:px-12 py-16 md:py-24 ${
        band ? 'bg-background-100 border-y border-background-200' : ''
      }`}
    >
      <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal className={reverse ? 'lg:order-2' : ''}>
          <div className="flex items-center gap-3 mb-5">
            <span className="font-data text-[13px] tracking-[0.2em] text-primary-600">{index}</span>
            <span className="w-8 h-px bg-primary-400"></span>
            <span className="text-[11px] font-data tracking-[0.2em] text-foreground-400">
              {stage ? stageLabels[stage] : 'SYSTEM STEP'}
            </span>
          </div>
          <h2 className="font-heading font-extrabold tracking-tight text-foreground-950 text-[32px] leading-[1.08] sm:text-[42px] lg:text-[46px]">
            {title}
          </h2>
          <p className="mt-4 font-heading font-semibold text-foreground-700 text-[17px] md:text-[19px] max-w-[520px]">
            {subtitle}
          </p>
          {body && (
            <p className="mt-4 text-[15px] leading-relaxed text-foreground-600 max-w-[520px]">
              {body}
            </p>
          )}
        </Reveal>

        <Reveal delay={140} className={reverse ? 'lg:order-1' : ''}>
          {children}
        </Reveal>
      </div>
    </section>
  );
}

export default StepSection;